import React, { useEffect, useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { Field, Fieldset, Label, ErrorMessage } from "../../../design/fieldset";
import { useMatter } from "../../../contexts/MatterContext";
import { Matter } from "../../../models/Matter";
import { Textarea } from "../../../design/textarea";

interface Props {
  matter: Matter;
  children?: React.ReactNode;
}

interface Errors {
  name?: string;
  matterType?: string;
}

const EditMatterButton: React.FC<Props> = ({ matter, children }) => {
  const { updateMatter } = useMatter();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState<string>(matter.name ?? "");
  // const [matterType, setMatterType] = useState<string>(matter.matterType ?? "");
  const [summary, setMatterSummary] = useState<string>(matter.summary ?? "");
  const [importantDetails, setImportantDetails] = useState<string>(
    matter.importantDetails ?? "",
  );
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setName(matter.name ?? "");
    setMatterSummary(matter.summary ?? "");
  }, [matter, isOpen]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!name.trim()) {
      newErrors.name = "A name is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);

    try {
      let data: {
        name: string;
        avatarUrl?: string;
        summary: string;
      } = {
        name,
        summary,
      };
      await updateMatter(matter._id, {
        ...data,
        // matterType,
        importantDetails,
      });
      clearForm();
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to update matter. Please try again later.");
    }

    setIsSaving(false);
  };

  const clearForm = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Button plain onClick={() => setIsOpen(true)}>
        {children ?? "Edit Matter"}
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Edit Matter</DialogTitle>
        <DialogBody>
          <Fieldset>
            <Field>
              <Label>Name</Label>
              <Input
                type="text"
                value={name}
                placeholder="Name"
                onChange={(e) => setName(e.target.value)}
              />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </Field>
            {/* <Field>
              <Label>Matter Type</Label>
              <Select
                value={matterType}
                onChange={(e) => setMatterType(e.target.value)}
              >
                <option value="">Select Matter Type</option>
                {matterTypes.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.name}
                  </option>
                ))}
              </Select>
            </Field> */}
            <Field>
              <Label>Matter Summary</Label>
              <Textarea
                rows={5}
                value={summary}
                placeholder="Matter Summary"
                onChange={(e) => setMatterSummary(e.target.value)}
              />
            </Field>
            <Field>
              <Label>Important Details</Label>
              <Textarea
                rows={5}
                value={importantDetails}
                placeholder="Important Details"
                onChange={(e) => setImportantDetails(e.target.value)}
              />
            </Field>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditMatterButton;
